import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Smartphone, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Unknown device parked at sign-in: polls until a trusted device approves. */
export function DeviceApprovalWait({ approval, onPoll, onUseCode, onBack }) {
  const [trust, setTrust] = useState(true);
  const [error, setError] = useState("");
  const [left, setLeft] = useState(approval.expires_in || 600);
  const [sendingCode, setSendingCode] = useState(false);
  const trustRef = useRef(trust);
  trustRef.current = trust;
  const pollRef = useRef(onPoll);
  pollRef.current = onPoll;

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      const r = await pollRef.current(approval.approval_id, approval.poll_token, trustRef.current);
      if (stop || r.ok || r.pending) return;
      stop = true; clearInterval(id);
      setError(r.error || "This sign-in request is no longer valid.");
    };
    const id = setInterval(tick, 3000);
    const cd = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    tick();
    return () => { stop = true; clearInterval(id); clearInterval(cd); };
  }, [approval.approval_id, approval.poll_token]);

  const mm = String(Math.floor(left / 60)).padStart(1, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <div className="space-y-5" data-testid="device-approval-wait">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center shrink-0"><ShieldCheck className="w-6 h-6" /></div>
        <div>
          <h1 className="text-xl font-display font-bold text-slate-900">Approve this sign-in on your other device</h1>
          <p className="text-sm text-slate-500 mt-1">
            Kingly hasn't seen this device before. Open Kingly on a device you already use — a card there asks “Was this you?”. Tap <b>Yes, that's me</b> and this screen continues on its own.
          </p>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 flex items-center gap-3" data-testid="device-approval-device">
        <Smartphone className="w-4 h-4 text-slate-400" />
        <span>This device: <b>{approval.device || "Unknown device"}</b>{approval.place ? ` · ${approval.place}` : ""}{approval.net ? ` · via ${approval.net.toUpperCase()}` : ""}</span>
      </div>
      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="device-approval-error">{error}</div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="device-approval-status">
          <Loader2 className="w-4 h-4 animate-spin text-amber-700" /> Waiting for approval… <span className="tabular-nums text-slate-400">{mm}:{ss}</span>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} className="accent-amber-600" data-testid="device-approval-trust" />
        Trust this device for 30 days
      </label>
      <div className="flex flex-col gap-2">
        <Button type="button" variant="outline" disabled={sendingCode || !!error} data-testid="device-approval-use-code" className="whitespace-normal h-auto py-2.5"
          onClick={async () => { setSendingCode(true); const r = await onUseCode(approval.approval_id, approval.poll_token); setSendingCode(false); if (!r.ok) setError(r.error); }}>
          {sendingCode ? "Sending…" : "I don't have my other device — text me a code"}
        </Button>
        <button type="button" onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 py-1" data-testid="device-approval-back">Back to sign in</button>
      </div>
    </div>
  );
}
